import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactFlow, { Controls, Background, MiniMap, addEdge, applyNodeChanges, applyEdgeChanges, MarkerType } from 'reactflow';
import 'reactflow/dist/style.css';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Plus, Loader2, Save, Trash2, Download, ArrowLeft, Database, Server, Globe, Users, Cpu } from 'lucide-react';
import { toast } from 'sonner';
import { getDataFlowMaps, createDataFlowMap, getDataFlowMap, updateDataFlowMap, deleteDataFlowMap } from '@/services/dpdpa';

const NODE_TYPES_META = {
  system: { label: 'System', color: '#3b82f6', icon: Server },
  database: { label: 'Database', color: '#8b5cf6', icon: Database },
  external: { label: 'External', color: '#ef4444', icon: Globe },
  process: { label: 'Process', color: '#f59e0b', icon: Cpu },
  user: { label: 'User/Principal', color: '#10b981', icon: Users },
};

const PII_CATEGORIES = ['personal_identifiers', 'contact_information', 'financial_data', 'health_data', 'biometric_data', 'behavioral_data', 'location_data'];

function FlowNode({ data }) {
  const meta = NODE_TYPES_META[data.node_type] || NODE_TYPES_META.system;
  const Icon = meta.icon;
  return (
    <div className="px-3 py-2 rounded-lg border-2 shadow-sm min-w-[120px] text-center" style={{ borderColor: meta.color, background: `${meta.color}10` }}>
      <div className="flex items-center justify-center gap-1.5 mb-1">
        <Icon className="w-3.5 h-3.5" style={{ color: meta.color }} />
        <span className="text-xs font-bold" style={{ color: meta.color }}>{meta.label}</span>
      </div>
      <p className="text-sm font-medium">{data.label}</p>
      {data.location && <p className="text-[10px] opacity-50">{data.location}</p>}
      {data.pii_categories?.length > 0 && (
        <div className="flex flex-wrap gap-0.5 mt-1 justify-center">
          {data.pii_categories.map(c => <span key={c} className="text-[8px] px-1 py-0 rounded bg-red-500/10 text-red-600">{c.split('_')[0]}</span>)}
        </div>
      )}
    </div>
  );
}

const nodeTypes = { custom: FlowNode };

export default function DataFlowPage() {
  const [maps, setMaps] = useState([]);
  const [currentMap, setCurrentMap] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddNode, setShowAddNode] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [nodeForm, setNodeForm] = useState({ label: '', node_type: 'system', description: '', pii_categories: [], location: 'India' });
  const [mapForm, setMapForm] = useState({ name: '', description: '' });

  const load = useCallback(async () => {
    try { setLoading(true); const d = await getDataFlowMaps(); setMaps(d.maps || []); } catch { toast.error('Failed to load'); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openMap = async (id) => {
    try {
      setLoading(true);
      const map = await getDataFlowMap(id);
      setCurrentMap(map);
      setNodes((map.nodes || []).map(n => ({ id: n.id, type: 'custom', position: { x: n.position_x || 0, y: n.position_y || 0 }, data: n })));
      setEdges((map.edges || []).map(e => ({ id: e.id, source: e.source_id, target: e.target_id, label: e.label, animated: e.cross_border, markerEnd: { type: MarkerType.ArrowClosed }, style: { stroke: e.cross_border ? '#ef4444' : '#6b7280' } })));
    } catch { toast.error('Failed to open'); } finally { setLoading(false); }
  };

  const handleCreateMap = async () => {
    if (!mapForm.name) return toast.error('Name required');
    try { const m = await createDataFlowMap(mapForm); toast.success('Map created'); setShowCreate(false); setMapForm({ name: '', description: '' }); openMap(m.id); } catch { toast.error('Failed'); }
  };

  const handleSave = async () => {
    if (!currentMap) return;
    try {
      setSaving(true);
      const saveNodes = nodes.map(n => ({ ...n.data, id: n.id, position_x: n.position.x, position_y: n.position.y }));
      const saveEdges = edges.map(e => ({ id: e.id, source_id: e.source, target_id: e.target, label: e.label || '', cross_border: e.animated || false }));
      await updateDataFlowMap(currentMap.id, { nodes: saveNodes, edges: saveEdges });
      toast.success('Saved');
    } catch { toast.error('Save failed'); } finally { setSaving(false); }
  };

  const handleAddNode = () => {
    if (!nodeForm.label) return toast.error('Label required');
    const id = `node_${Date.now()}`;
    const newNode = {
      id, type: 'custom',
      position: { x: Math.random() * 400 + 50, y: Math.random() * 300 + 50 },
      data: { ...nodeForm, id },
    };
    setNodes(prev => [...prev, newNode]);
    setShowAddNode(false);
    setNodeForm({ label: '', node_type: 'system', description: '', pii_categories: [], location: 'India' });
  };

  const onNodesChange = useCallback((changes) => setNodes(nds => applyNodeChanges(changes, nds)), []);
  const onEdgesChange = useCallback((changes) => setEdges(eds => applyEdgeChanges(changes, eds)), []);
  const onConnect = useCallback((params) => {
    setEdges(eds => addEdge({ ...params, id: `edge_${Date.now()}`, markerEnd: { type: MarkerType.ArrowClosed }, animated: false, style: { stroke: '#6b7280' } }, eds));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin opacity-50" /></div>;

  // Map list view
  if (!currentMap) {
    return (
      <div className="p-6 space-y-6" data-testid="dataflow-list-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Data Flow Mapping</h1>
            <p className="text-sm opacity-60">Visual data lineage editor — map system-to-system data flows and PII categories</p>
          </div>
          <Button onClick={() => setShowCreate(true)} data-testid="create-map-btn"><Plus className="w-4 h-4 mr-2" /> New Data Flow Map</Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {maps.map(m => (
            <Card key={m.id} className="cursor-pointer hover:border-blue-500/50 transition-colors" onClick={() => openMap(m.id)} data-testid={`map-card-${m.id}`}>
              <CardContent className="p-4">
                <p className="font-medium">{m.name}</p>
                <p className="text-xs opacity-50 mt-1">{m.description || 'No description'}</p>
                <div className="flex items-center gap-2 mt-2">
                  <Badge variant="secondary">{(m.nodes || []).length} nodes</Badge>
                  <Badge variant="secondary">{(m.edges || []).length} flows</Badge>
                </div>
                <p className="text-[10px] opacity-40 mt-2">{new Date(m.created_at).toLocaleDateString()}</p>
              </CardContent>
            </Card>
          ))}
          {maps.length === 0 && <p className="col-span-3 text-center py-12 opacity-50">No data flow maps yet. Create one to start mapping your data landscape.</p>}
        </div>
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogContent>
            <DialogHeader><DialogTitle>Create Data Flow Map</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Name</Label><Input value={mapForm.name} onChange={e => setMapForm(p => ({ ...p, name: e.target.value }))} data-testid="map-name-input" /></div>
              <div><Label>Description</Label><Input value={mapForm.description} onChange={e => setMapForm(p => ({ ...p, description: e.target.value }))} /></div>
            </div>
            <DialogFooter><Button onClick={handleCreateMap} data-testid="create-map-submit">Create</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Flow editor view
  return (
    <div className="p-6 space-y-4" data-testid="dataflow-editor">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => { setCurrentMap(null); load(); }}><ArrowLeft className="w-4 h-4" /></Button>
          <div>
            <h1 className="text-lg font-bold">{currentMap.name}</h1>
            <p className="text-xs opacity-50">{currentMap.description || 'Drag nodes to arrange, connect by dragging between handles'}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowAddNode(true)} data-testid="add-node-btn"><Plus className="w-3 h-3 mr-1" /> Add Node</Button>
          <Button size="sm" onClick={handleSave} disabled={saving} data-testid="save-flow-btn">
            {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />} Save
          </Button>
        </div>
      </div>

      {/* Node type legend */}
      <div className="flex gap-3 flex-wrap">
        {Object.entries(NODE_TYPES_META).map(([key, meta]) => (
          <div key={key} className="flex items-center gap-1.5 text-xs">
            <div className="w-2.5 h-2.5 rounded-full" style={{ background: meta.color }} />
            <span>{meta.label}</span>
          </div>
        ))}
        <span className="text-xs opacity-40 ml-2">Red animated edge = cross-border flow</span>
      </div>

      <div className="border rounded-lg" style={{ height: 500 }}>
        <ReactFlow
          nodes={nodes} edges={edges}
          onNodesChange={onNodesChange} onEdgesChange={onEdgesChange} onConnect={onConnect}
          nodeTypes={nodeTypes} fitView
        >
          <Controls />
          <MiniMap />
          <Background variant="dots" gap={16} size={1} />
        </ReactFlow>
      </div>

      {/* Add Node Dialog */}
      <Dialog open={showAddNode} onOpenChange={setShowAddNode}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Data Flow Node</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Label</Label><Input value={nodeForm.label} onChange={e => setNodeForm(p => ({ ...p, label: e.target.value }))} data-testid="node-label-input" /></div>
            <div><Label>Type</Label>
              <Select value={nodeForm.node_type} onValueChange={v => setNodeForm(p => ({ ...p, node_type: v }))}>
                <SelectTrigger data-testid="node-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(NODE_TYPES_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Location</Label><Input value={nodeForm.location} onChange={e => setNodeForm(p => ({ ...p, location: e.target.value }))} /></div>
            <div>
              <Label>PII Categories</Label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {PII_CATEGORIES.map(c => (
                  <Button key={c} size="sm" variant={nodeForm.pii_categories.includes(c) ? 'default' : 'outline'} className="text-[10px] h-6 px-2"
                    onClick={() => setNodeForm(p => ({ ...p, pii_categories: p.pii_categories.includes(c) ? p.pii_categories.filter(x => x !== c) : [...p.pii_categories, c] }))}>
                    {c.replace(/_/g, ' ')}
                  </Button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter><Button onClick={handleAddNode} data-testid="add-node-submit">Add Node</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
